import { useState } from "react";
import { Megaphone, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { NEW_PRODUCT_URL } from "@/config/windDown";

const DISMISS_KEY = "winddown-banner-dismissed";

/**
 * Landing-page announcement bar for the controlled wind-down: the product is
 * closing and we're moving users to our new app. Reassures subscribers (no more
 * charges, access until the shutdown date) and drives them to the new product.
 * Dismissible (persisted in localStorage). Rendered at the very top of Layout
 * on the landing route only.
 */
export function WindDownBanner() {
  const { t, isRTL } = useLanguage();
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "true",
  );

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t.common.windDownBannerText}
      className="relative w-full bg-gradient-to-r gradient-primary text-white"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-1.5 px-10 py-2.5 text-center sm:flex-row sm:gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Megaphone className="h-4 w-4 shrink-0" />
          {t.common.windDownBannerText}
        </span>
        <span className="text-xs font-normal text-white/85">
          {t.common.windDownBannerSubscriberNote}
        </span>
        <a
          href={NEW_PRODUCT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-3.5 py-1 text-sm font-bold transition-colors hover:bg-white/30"
        >
          {t.common.windDownBannerCta}
          <ArrowRight className={cn("h-3.5 w-3.5", isRTL && "rotate-180")} />
        </a>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className={cn(
          "absolute top-1/2 -translate-y-1/2 rounded-full p-1 transition-colors hover:bg-white/20",
          isRTL ? "left-2" : "right-2",
        )}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
